import { connect } from 'react-redux';
import {
    injectIntl,
    FormattedMessage,
    FormattedDate,
    WrappedComponentProps
} from 'react-intl';

import { FormattedCurrency } from '../../../../iwIntlProvider/FormattedCurrency';

import DashboardCTA from '../DashboardCTA/DashboardCTA';
import RepaidPercentageIcon from './RepaidPercentageIcon';

import messages from './PastRepayments.messages';
import styles from './PastRepayments.module.css';
import { REVENUE_BASED_LOAN_PRODUCT_TYPE } from '../../accountsSelector';
import { RootState } from '@iwProvider/configureStore';
import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import { useQuery } from '@tanstack/react-query';
import _ from 'lodash';
import { getAccountsQueryConfig } from '@api/lendingAPI/queryConfigs';
import { NEW_IwLink } from '@iwRouter/NEW_IwLink';

const mapStateToProps = (state: RootState) => ({
    locale: state.siteContext.locale
});

type PropsFromRedux = ReturnType<typeof mapStateToProps> & {
    className?: string;
    accountId: string;
};

const PastRepayments = ({
    className,
    accountId
}: PropsFromRedux & WrappedComponentProps) => {
    const stateKey = useStateKey();

    const {
        data: getAccountsResponse,
        isLoading,
        isError
    } = useQuery(getAccountsQueryConfig(stateKey));

    if (isLoading || isError) return null;

    const account = getAccountsResponse.data!.accounts.find(
        (account) => account.account_id === accountId
    )!;
    const productType = account.product_type;

    const latestFunding = _.findLast(account.cashflows, {
        type: 'repayment'
    });
    const lastRepaymentDate = latestFunding ? latestFunding.datetime : null;

    const currentPrincipal = account.balance!.principal!;

    const loanAmount = account.loan_amount!;

    const loanFee = account.revenue_based_loan_details?.loan_fee || 0;

    const leftToPay = account.balance!.total!;

    const isRBL = productType === REVENUE_BASED_LOAN_PRODUCT_TYPE;
    const totalLoanAmount = isRBL ? loanAmount + loanFee : loanAmount;
    const totalAmountRepaid = isRBL
        ? totalLoanAmount - leftToPay
        : loanAmount - currentPrincipal;
    const totalProgress = isRBL
        ? 1 - leftToPay / totalLoanAmount
        : 1 - currentPrincipal / loanAmount;

    return (
        <>
            <DashboardCTA
                title={<FormattedMessage {...messages.pastRepaymentsTitle} />}
                className={className}
                icon={
                    <RepaidPercentageIcon
                        radius={22}
                        stroke={3}
                        progress={totalProgress}
                    />
                }
                data-testid="past-repayments-action"
                description={
                    <>
                        <FormattedMessage
                            {...messages.totalPastRepayments}
                            values={{
                                amountRepaid: (
                                    <span className={styles.amountRepaid}>
                                        <FormattedCurrency
                                            value={totalAmountRepaid}
                                        />
                                    </span>
                                ),
                                loanAmount: (
                                    <strong>
                                        <FormattedCurrency
                                            value={totalLoanAmount}
                                        />
                                    </strong>
                                )
                            }}
                        />{' '}
                        {totalAmountRepaid > 0 && (
                            <FormattedMessage
                                {...messages.lastRepayment}
                                values={{
                                    lastRepaymentDate: (
                                        <strong>
                                            <FormattedDate
                                                day="numeric"
                                                month="short"
                                                year="numeric"
                                                value={
                                                    // @ts-expect-error
                                                    new Date(lastRepaymentDate)
                                                }
                                            />
                                        </strong>
                                    )
                                }}
                            />
                        )}
                    </>
                }
                cta={
                    <NEW_IwLink
                        data-testid="see-past-repayments"
                        to={{ pathname: '/history/' }}
                        className={styles.link}
                    >
                        <FormattedMessage {...messages.pastRepaymentCta} />
                    </NEW_IwLink>
                }
            />
        </>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps)(injectIntl(PastRepayments));
